import React, { useEffect, useMemo, useState } from "react";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import Message from "shared/Message";
import ButtonRounded from "shared/Button";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuth } from "contexts/auth";
import { environments } from "constants/environments";
import { IApiMetaData } from "interfaces/apiMetaData";
import { callApi } from "apiServices/callApi";
import {
  IUpdateSubCategoryInputs,
  updateSubCategorySchema,
} from "schema/subCategory";

interface DropdownItem {
  name: string;
  value: boolean | string;
}

interface IProps {
  rowData: any;
  onSave: () => void;
}

const UpdateCategory = ({ rowData, onSave }: IProps) => {
  const [categories, setCategories] = useState<DropdownItem[]>([]);
  const [isResponse, setIsResponse] = useState(false);
  const [result, setResult] = useState<IApiMetaData | any>(null); // Type-safe result
  const { sharedData } = useAuth();
  const categoryAPI =
    environments.domain + sharedData?.apisList?.WebSite?.Category?.General;
  const subCategoryAPI =
    environments.domain + sharedData?.apisList?.WebSite?.SubCategory?.General;

  useEffect(() => {
    const getCategories = async () => {
      try {
        const response: IApiMetaData = await callApi(
          "GET",
          categoryAPI,
          {},
          {}
        );
        setCategories(
          response?.Data?.Content?.map((category: any) => ({
            name: category.name,
            value: category._id,
          }))
        );
      } catch (error) {
        // Handle error fetching categories
      }
    };
    getCategories();
  }, [categoryAPI]);

  const {
    handleSubmit,
    register,
    control,
    formState: { errors, isSubmitting },
  } = useForm<IUpdateSubCategoryInputs>({
    resolver: zodResolver(updateSubCategorySchema),
    defaultValues: {
      ...rowData,
      category: rowData?.category?._id, // Ensuring default values are properly set
    },
    mode: "onChange",
  });

  const onSubmit: SubmitHandler<IUpdateSubCategoryInputs> = async (data) => {
    setIsResponse(false);
    try {
      const response: IApiMetaData = await callApi(
        "PUT",
        subCategoryAPI,
        data,
        {},
        { id: String(rowData?._id) }
      );
      setResult(response);
      if (response.Status === 201) {
        onSave(); // Call onSave only if the API request is successful
      }
    } catch (error: any) {
      setResult({
        Status: error.response?.status || 500,
        Message:
          error.response?.data?.Error ||
          "An error occurred while updating the category",
        Severity: "error",
      });
    }
    setIsResponse(true);
  };

  const status = useMemo<DropdownItem[]>(
    () => [
      { name: "Active", value: true },
      { name: "Not Active", value: false },
    ],
    []
  );

  return (
    <div className="grid justify-content-center">
      <div className="col-12">
        <div className="px-3">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="p-fluid formgrid p-4 grid border-round-md">
              <div className="field col-12 md:col-12 lg:col-6 xl:col-6">
                <label htmlFor="name">Name</label>
                <InputText
                  id="name"
                  type="text"
                  {...register("name", {
                    setValueAs: (v) => v?.trim(),
                  })}
                />
                {errors?.name && (
                  <small className="text-error text-red-600">
                    {errors?.name?.message}
                  </small>
                )}
              </div>

              <div className="field col-12 md:col-6">
                <label htmlFor="Module">Module</label>
                <Controller
                  name="category"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <>
                      <Dropdown
                        id="category"
                        value={value} // Pass value from form
                        options={categories}
                        onChange={(e) => onChange(e.value)}
                        optionLabel="name"
                        optionValue="value"
                        placeholder="Select Module Name"
                      />
                      {errors?.category && (
                        <small className="text-error text-red-600">
                          {errors?.category?.message}
                        </small>
                      )}
                    </>
                  )}
                />
              </div>

              <div className="field col-12 md:col-12 lg:col-6 xl:col-6">
                <label htmlFor="status">Status</label>
                <Controller
                  name="isActive"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <>
                      <Dropdown
                        id="status"
                        value={value} // Pass value from form
                        options={status}
                        onChange={(e) => onChange(e.value)}
                        optionLabel="name"
                        optionValue="value"
                        placeholder="Select Status"
                      />
                      {errors?.isActive && (
                        <small className="text-error text-red-600">
                          {errors?.isActive?.message}
                        </small>
                      )}
                    </>
                  )}
                />
              </div>

              <div className="text-right mt-4 ml-auto">
                <ButtonRounded
                  disabled={isSubmitting}
                  label="Save"
                  raised
                  severity="success"
                />
              </div>
            </div>
          </form>
        </div>
      </div>
      {isResponse && (
        <div className="col-8">
          <Message
            message={result?.Message}
            severity={result?.Status === 201 ? "success" : "error"}
          />
        </div>
      )}
    </div>
  );
};

export default UpdateCategory;
